import { useCallback } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { SHIFT, TIME_OFF, UNAVAILABILITY, NO_SHOW, SICK_CALLOUT } from '@sdk/schedule/constants';
import type { EventType } from '@sdk/schedule/types/EventType';
import type { EventBoxCommonBaseProps } from '@sdk/schedule/types/EventBoxProps';

import type { OpenEventCallback, IsShiftLineVisibleType } from './types';
import type { IsEventSelectedType, ToggleEventType } from './hooks/useEventSelection';
import ShiftBox from './ShiftBox';
import TimeOffBox from './TimeOffBox';
import UnavailabilityBox from './UnavailabilityBox';
import NoShowBox from './NoShowBox';
import SickCalloutBox from './SickCalloutBox';

const EventBoxContainer = styled.div`
  margin-bottom: 4px;
  min-width: 0;
  width: 100%;
`;

type Props = {
  event: EventType;
  openEvent: OpenEventCallback;
  toggleEvent: ToggleEventType;
  isEventSelected: IsEventSelectedType;
  hasSelectedEvents: boolean;
  isExchange: boolean;
  isShiftLineVisible: IsShiftLineVisibleType;
  isHovered: boolean;
} & EventBoxCommonBaseProps;

const EventBox: React.FC<React.PropsWithChildren<Props>> = ({
  event,
  openEvent,
  shiftAppearanceMode,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isExchange,
  isShiftLineVisible,
  hoverComponent,
  height,
  showPlus,
  isHovered,
}) => {
  const toggleSelection = useCallback(() => {
    toggleEvent(event.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  const renderItem = (): JSX.Element => {
    if (event.type === SHIFT) {
      return (
        <ShiftBox
          shift={event}
          openEvent={openEvent}
          shiftAppearanceMode={shiftAppearanceMode}
          hoverComponent={hoverComponent}
          toggleSelection={toggleSelection}
          showSelect={hasSelectedEvents}
          isSelected={isEventSelected(event.id)}
          isExchange={isExchange}
          isShiftLineVisible={isShiftLineVisible}
          height={height}
          showPlus={showPlus}
          isHovered={isHovered}
        />
      );
    }

    if (event.type === TIME_OFF) {
      return (
        <TimeOffBox
          timeOff={event}
          hoverComponent={hoverComponent}
          openEvent={openEvent}
          shiftAppearanceMode={shiftAppearanceMode}
          height={height}
          showPlus={showPlus}
        />
      );
    }

    if (event.type === UNAVAILABILITY) {
      return (
        <UnavailabilityBox
          unavailability={event}
          hoverComponent={hoverComponent}
          openEvent={openEvent}
          shiftAppearanceMode={shiftAppearanceMode}
          height={height}
          showPlus={showPlus}
        />
      );
    }

    if (event.type === NO_SHOW) {
      return (
        <NoShowBox
          noShow={event}
          shiftAppearanceMode={shiftAppearanceMode}
          hoverComponent={hoverComponent}
          openEvent={openEvent}
          toggleSelection={toggleSelection}
          showSelect={hasSelectedEvents}
          isSelected={isEventSelected(event.id)}
          height={height}
          showPlus={showPlus}
          isHovered={isHovered}
        />
      );
    }

    if (event.type === SICK_CALLOUT) {
      return (
        <SickCalloutBox
          sickCallout={event}
          shiftAppearanceMode={shiftAppearanceMode}
          hoverComponent={hoverComponent}
          openEvent={openEvent}
          toggleSelection={toggleSelection}
          showSelect={hasSelectedEvents}
          isSelected={isEventSelected(event.id)}
          height={height}
          showPlus={showPlus}
          isHovered={isHovered}
        />
      );
    }

    return null;
  };

  return <EventBoxContainer>{renderItem()}</EventBoxContainer>;
};

export default EventBox;
